import { h, Fragment } from "preact";
import { useMemo } from "preact/hooks";

import NoteStatus from "../../../foundation/model/NoteStatus";
import classname from "../../../foundation/utils/classname";
import Separator from "../../Sidebar/Separator";
import IconApproval from "../Icons/IconApproval";
import IconMenu from "../Icons/IconMenu";
import AddReactionButton from "../AddReactionButton";
import DotColors from "../DotColors";
import DropdownMenu from "../DropdownMenu";
import Menu from "../Menu";
import OnlyIconButton, { OnlyIconButtonVariant } from "../OnlyIconButton";
import Tooltip from "../Tooltip";

import styles from "./CommentOptions.module.css";

interface Props {
    class?: string;
    isApprovalsMode?: boolean;
    isEditing?: boolean;
    isFirstComment: boolean;
    isUnread: boolean;
    isOwner?: boolean;
    canRemoveNotes?: boolean;
    dotStatus: NoteStatus;
    selectedColor?: string;
    onSelectReaction(emoji: string): void;
    onChangeColor?(color: string): void;
    onMenuStateChange(isOpen: boolean): void;
    onCopyLink(): void;
    onEdit?(): void;
    onUpdateStatus(): void;
    onUpdateReadStatus?(): void;
    onDelete?(): void;
}

export default function CommentOptions({
    class: cssClass,
    isApprovalsMode = false,
    isEditing = false,
    isFirstComment,
    isUnread,
    isOwner = false,
    canRemoveNotes = false,
    dotStatus,
    selectedColor,
    onSelectReaction,
    onChangeColor,
    onMenuStateChange,
    onCopyLink,
    onEdit,
    onUpdateStatus,
    onUpdateReadStatus,
    onDelete
}: Props) {
    const shouldDisplayResolve = useMemo(() =>
        !isApprovalsMode && isFirstComment && dotStatus === NoteStatus.OPEN
    , [isApprovalsMode, isFirstComment, dotStatus]);

    const shouldDisplayReopen = useMemo(() =>
        !isApprovalsMode && isFirstComment && dotStatus === NoteStatus.RESOLVED
    , [isApprovalsMode, isFirstComment, dotStatus]);

    const shouldDisplayMarkAsRead = useMemo(() =>
        !isApprovalsMode && isFirstComment && onUpdateReadStatus !== undefined
    , [isApprovalsMode, isFirstComment, onUpdateReadStatus]);

    const shouldDisplayEdit = useMemo(() =>
        isOwner && dotStatus === NoteStatus.OPEN && onEdit !== undefined
    , [dotStatus, isOwner]);

    const shouldDisplayDelete = useMemo(() =>
        (isOwner || canRemoveNotes) && onDelete !== undefined
    , [canRemoveNotes, isOwner]);

    function handleReactionPopoverOpen() {
        onMenuStateChange(true);
    }

    function handleReactionPopoverClose() {
        onMenuStateChange(false);
    }

    return (
        <div class={classname(styles.commentOptions, cssClass, {
            [styles.editing]: isEditing,
            [styles.firstComment]: isFirstComment
        })}>
            {shouldDisplayResolve && (
                <Tooltip
                    targetClass={classname(styles.commentOptionButton, styles.resolveButton)}
                    content="Resolve comment"
                    hideDelay={50}>
                    <OnlyIconButton
                        variant={OnlyIconButtonVariant.Ninja}
                        // eslint-disable-next-line react/jsx-no-bind
                        icon={() => <IconApproval type="small"/>}
                        onClick={onUpdateStatus}/>
                </Tooltip>
            )}
            <Separator
                class={classname(styles.commentOptionSeparator, styles.resolveSeperator)}
                vertical/>
            {dotStatus === NoteStatus.OPEN && (
                <Tooltip
                    targetClass={styles.commentOptionButton}
                    content="Add reaction"
                    hideDelay={50}>
                    <AddReactionButton
                        allowMultiSelection
                        onOpen={handleReactionPopoverOpen}
                        onClose={handleReactionPopoverClose}
                        onSelectReaction={onSelectReaction}/>
                </Tooltip>
            )}
            <Tooltip
                targetClass={styles.commentOptionButton}
                content="More options"
                hideDelay={50}>
                <DropdownMenu
                    buttonProps={{
                        icon: IconMenu,
                        showChevron: false
                    }}
                    onMenuStateChange={onMenuStateChange}>
                    {shouldDisplayMarkAsRead && (
                        <>
                            <Menu.Button onClick={onUpdateReadStatus}>
                                {`Mark as ${isUnread ? "read" : "unread"}`}
                            </Menu.Button>
                            <Menu.Divider/>
                        </>
                    )}
                    {shouldDisplayEdit && (
                        <Menu.Button onClick={onEdit}>
                            Edit comment
                        </Menu.Button>
                    )}
                    {shouldDisplayReopen && (
                        <>
                            <Menu.Button onClick={onUpdateStatus}>
                                Reopen comment
                            </Menu.Button>
                            <Menu.Divider/>
                        </>
                    )}
                    <Menu.Button onClick={onCopyLink}>
                        Copy link
                    </Menu.Button>
                    {shouldDisplayDelete && (
                        <>
                            <Menu.Divider/>
                            <Menu.Button warning onClick={onDelete}>
                                Delete comment
                            </Menu.Button>
                        </>
                    )}
                </DropdownMenu>
            </Tooltip>
            <Separator
                class={classname(styles.commentOptionSeparator, styles.dotColorsSeparator)}
                vertical/>
            {isFirstComment && selectedColor !== undefined && onChangeColor !== undefined && (
                <DotColors
                    class={styles.commentDotColors}
                    isEditing={isEditing}
                    selectedColor={selectedColor}
                    onClick={onChangeColor}/>
            )}
        </div>
    );
}
